<template>
<div class="w-full min-w-0">




      <div class="space-y-4">
        <div
          v-if="error"
          class="px-4 py-2 text-xs border rounded-lg border-amber-200 bg-amber-50 text-amber-700"
        >
          {{ error }}
        </div>

         <!-- Row 1: donations over time + blood group distribution -->
         <div class="grid gap-4 lg:grid-cols-3">
           <section class="p-5 bg-white border rounded-xl border-[#e5e7eb] lg:col-span-2 relative">
             <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
               <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
             </div>
             <div class="flex items-center justify-between">
              <h2 class="text-xs font-semibold text-slate-600">Donations over time</h2>
              <div class="flex items-center gap-1.5">
                <button
                  type="button"
                  aria-label="Earlier months"
                  class="flex items-center justify-center w-7 h-7 rounded-md text-[#0f2a6b] hover:bg-[#0f2a6b]/10"
                  @click="earlierMonths"
                >
                  <svg class="w-4 h-4" aria-hidden="true" viewBox="0 0 24 24" fill="none">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m15 19-7-7 7-7" />
                  </svg>
                </button>
                <span class="text-[11px] text-slate-500">{{ rangeLabel }}</span>
                <button
                  type="button"
                  aria-label="Later months"
                  class="flex items-center justify-center w-7 h-7 rounded-md text-[#0f2a6b] hover:bg-[#0f2a6b]/10 disabled:pointer-events-none disabled:opacity-40"
                  :disabled="monthsBack === 0"
                  @click="laterMonths"
                >
                  <svg class="w-4 h-4" aria-hidden="true" viewBox="0 0 24 24" fill="none">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m9 5 7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="flex items-baseline gap-2 mt-2">
              <span class="text-3xl font-bold text-[#0f2a6b]">{{ totalDonations }}</span>
              <span class="text-xs text-slate-500">donations ·</span>
              <span class="text-xs font-semibold text-green-600">{{ approvedPct }}% approved</span>
            </div>

            <ul class="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-slate-600">
              <li v-for="s in statusSummary" :key="s.label" class="flex items-center gap-1.5">
                <span class="w-2.5 h-2.5 rounded-sm" :style="{ backgroundColor: s.color }"></span>
                {{ s.label }}
                <span class="font-semibold text-slate-900">{{ s.count }}</span>
              </li>
            </ul>

            <div class="flex mt-4">
              <!-- Y axis -->
              <div class="relative w-8 h-[140px] shrink-0">
                <span
                  v-for="tick in yTicks"
                  :key="tick"
                  class="absolute right-2 text-[10px] text-slate-500 translate-y-1/2"
                  :style="{ bottom: (tick / chartMax) * 100 + '%' }"
                >
                  {{ tick }}
                </span>
              </div>

              <!-- Plot -->
              <div class="flex-1 min-w-0">
                <div class="relative h-[140px]">
                  <div
                    v-for="tick in yTicks"
                    :key="'grid-' + tick"
                    class="absolute left-0 right-0 border-t border-[#eef1f5]"
                    :style="{ bottom: (tick / chartMax) * 100 + '%' }"
                  ></div>

                  <div class="relative flex h-full">
                    <div
                      v-for="month in monthly"
                      :key="month.key"
                      class="flex flex-col-reverse items-center flex-1 h-full"
                      :class="{ 'border-l border-dashed border-[#cbd3e1]': month.yearStart }"
                    >
                      <div
                        v-for="seg in month.segments"
                        :key="seg.label"
                        class="flex-none w-[50%] max-w-[30px]"
                        :style="{
                          height: (seg.count / chartMax) * 100 + '%',
                          backgroundColor: seg.color,
                        }"
                        :title="`${month.label} ${month.year} · ${seg.label}: ${seg.count}`"
                      ></div>
                    </div>
                  </div>
                </div>

                <div class="flex pt-2 border-t border-[#e5e7eb]">
                  <span
                    v-for="month in monthly"
                    :key="'label-' + month.key"
                    class="flex-1 text-[10px] text-center text-slate-500"
                    :class="{ 'border-l border-dashed border-[#cbd3e1]': month.yearStart }"
                  >
                    {{ month.label }}
                  </span>
                </div>

                <!-- Years, each spanning its months -->
                <div class="flex mt-1">
                  <div
                    v-for="group in yearGroups"
                    :key="group.year"
                    class="pt-1 mx-0.5 text-[11px] font-semibold text-center text-[#0f2a6b] border-t-2 border-[#0f2a6b]/25"
                    :style="{ flex: group.count }"
                  >
                    {{ group.year }}
                  </div>
                </div>
              </div>
            </div>
          </section>

           <section class="p-5 bg-white border rounded-xl border-[#e5e7eb] relative">
             <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
               <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
             </div>
             <h2 class="text-xs text-slate-500">Blood group distribution</h2>

            <ul class="mt-4 space-y-2.5">
              <li
                v-for="row in bloodGroupRows"
                :key="row.label"
                class="flex items-center gap-3"
              >
                <span class="w-[74px] text-xs text-slate-800 shrink-0">{{ row.label }}</span>
                <div class="flex-1 h-2 rounded-full bg-[#eef1f5]">
                  <div
                    class="h-full rounded-full"
                    :style="{
                      width: (row.count / bloodGroupMax) * 100 + '%',
                      backgroundColor: row.label === 'Unknown' ? '#9aa3c0' : '#0f2a6b',
                    }"
                  ></div>
                </div>
                <span class="w-5 text-xs font-semibold text-right text-slate-900">{{ row.count }}</span>
              </li>
            </ul>
          </section>
        </div>

         <!-- Row 2: status / gender / process / age -->
         <section
           class="grid bg-white border rounded-xl border-[#e5e7eb] lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-[#e5e7eb] relative"
         >
           <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
             <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
           </div>
           <div v-for="b in breakdowns" :key="b.title" class="p-4">
            <p class="text-[11px] text-slate-500">{{ b.title }}</p>
            <div class="flex w-full h-1.5 mt-2 overflow-hidden rounded-full bg-slate-100">
              <div
                v-for="item in b.items"
                :key="item.label"
                class="h-full"
                :style="{ width: item.share + '%', backgroundColor: item.color }"
              ></div>
            </div>
            <ul class="mt-3 space-y-1.5">
              <li
                v-for="item in b.items"
                :key="item.label"
                class="flex items-center justify-between text-[11px]"
              >
                <span class="flex items-center gap-1.5 text-slate-700">
                  <span class="w-1.5 h-1.5 rounded-full" :style="{ backgroundColor: item.color }"></span>
                  {{ item.label }}
                </span>
                <span>
                  <span class="font-semibold text-slate-900">{{ item.count }}</span>
                  <span class="text-slate-400"> · {{ item.percent }}%</span>
                </span>
              </li>
            </ul>
          </div>

           <div class="p-4">
             <p class="text-[11px] text-slate-500">Age</p>
             <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
               <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
             </div>
             <div class="flex items-end gap-2 mt-2 h-[72px]">
              <div
                v-for="row in ageRows"
                :key="row.label"
                class="flex flex-col items-center justify-end flex-1 h-full"
              >
                <span class="mb-0.5 text-[10px] font-semibold text-slate-800">{{ row.count }}</span>
                <div
                  class="w-full rounded-t-[3px] bg-[#0f2a6b]"
                  :style="{ height: (row.count / ageMax) * 52 + 'px' }"
                ></div>
              </div>
            </div>
            <div class="flex gap-2 mt-1 border-t border-[#e5e7eb] pt-1">
              <span
                v-for="row in ageRows"
                :key="'age-' + row.label"
                class="flex-1 text-[9px] text-center text-slate-500"
              >
                {{ row.label }}
              </span>
            </div>
          </div>
        </section>

           <!-- Donation records -->
         <div>
           <div class="flex items-center justify-between mb-3">
             <h2 class="text-sm font-bold text-[#0f2a6b]">Donation records</h2>
             <span class="text-[11px] text-slate-500">{{ totalRecords }} records</span>
           </div>

           <div
             class="overflow-auto max-h-[440px] bg-white border rounded-xl border-[#e5e7eb] relative transition-opacity"
             :class="{ 'opacity-60': loading }"
           >
             <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-white/70 rounded-xl">
               <div class="w-6 h-6 border-2 rounded-full border-custom-yellow border-t-transparent animate-spin"></div>
             </div>
             <table class="w-full min-w-[900px] text-left border-collapse">
              <thead>
                <tr class="text-xs bg-[#f8fafc] border-b border-[#e5e7eb] text-slate-800">
                  <th scope="col" class="w-10 p-4">
                    <input
                      id="checkbox-all-search"
                      type="checkbox"
                      class="w-4 h-4 border-gray-300 rounded accent-[#0f2a6b] text-custom-blue focus:ring-custom-blue"
                      :checked="allSelected"
                      @change="toggleAll"
                    />
                    <label for="checkbox-all-search" class="sr-only">Select all</label>
                  </th>
                  <th scope="col" class="px-3 py-3 font-semibold">Serial no.</th>
                  <th scope="col" class="px-3 py-3 font-semibold">Donor Name</th>
                  <th scope="col" class="px-3 py-3 font-semibold">Age</th>
                  <th scope="col" class="px-3 py-3 font-semibold">Gender</th>
                  <th scope="col" class="px-3 py-3 font-semibold">Blood Group</th>
                  <th scope="col" class="px-3 py-3 font-semibold">Donation Process</th>
                  <th scope="col" class="px-3 py-3 font-semibold">Donation Date</th>
                  <th scope="col" class="px-3 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(donation, index) in donations"
                  :key="donation.donation_id"
                  class="border-b border-[#eef1f5] text-xs text-slate-700"
                >
                  <td class="p-4">
                    <input
                      :id="'checkbox-table-search-' + index"
                      v-model="selected"
                      type="checkbox"
                      :value="donation.donation_id"
                      class="w-4 h-4 border-gray-300 rounded accent-[#0f2a6b] text-custom-blue focus:ring-custom-blue"
                    />
                    <label :for="'checkbox-table-search-' + index" class="sr-only">Select row</label>
                  </td>
                  <td class="px-3 py-2.5">{{ donation.serial || "N/A" }}</td>
                  <td class="px-3 py-2.5">{{ donation.donor_name || "N/A" }}</td>
                  <td class="px-3 py-2.5">{{ donation.age || "N/A" }}</td>
                  <td class="px-3 py-2.5">{{ donation.gender || "N/A" }}</td>
                  <td class="px-3 py-2.5">{{ donation.blood_group || "Unknown" }}</td>
                  <td class="px-3 py-2.5">{{ donation.process || "N/A" }}</td>
                  <td class="px-3 py-2.5 whitespace-nowrap">{{ formatDate(donation.date_added) }}</td>
                  <td class="px-3 py-2.5">
                    <span
                      class="px-2 py-1 text-[11px] font-medium rounded"
                      :class="statusClass(donation.status)"
                    >
                      {{ donation.status || "N/A" }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!donations.length && !loading">
                  <td colspan="9" class="py-10 text-sm text-center text-slate-400">
                    No donations match these filters.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="flex items-center justify-center gap-3 mt-4">
            <button
              type="button"
              class="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#0f2a6b] rounded-lg select-none hover:bg-[#0f2a6b]/10 disabled:pointer-events-none disabled:opacity-40"
              :disabled="currentPage === 1"
              @click="previousPage"
            >
              <svg class="w-4 h-4" aria-hidden="true" viewBox="0 0 24 24" fill="none">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m15 19-7-7 7-7" />
              </svg>
              Previous
            </button>

            <div class="flex items-center gap-1">
              <template v-for="(item, i) in pageItems">
                <span v-if="item === '…'" :key="'gap-' + i" class="px-1 text-xs text-slate-500">…</span>
                <button
                  v-else
                  :key="item"
                  type="button"
                  class="w-8 h-8 text-xs font-semibold transition-colors rounded-md select-none"
                  :class="
                    item === currentPage
                      ? 'bg-[#b9c3e6] text-[#0f2a6b]'
                      : 'text-[#0f2a6b] hover:bg-[#0f2a6b]/10'
                  "
                  @click="goToPage(item)"
                >
                  {{ item }}
                </button>
                </template>


            </div>
            <button
              type="button"
              class="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-[#0f2a6b] rounded-lg select-none hover:bg-[#0f2a6b]/10 disabled:pointer-events-none disabled:opacity-40"
              :disabled="currentPage === totalPages"
              @click="nextPage"
            >
              Next
              <svg class="w-4 h-4" aria-hidden="true" viewBox="0 0 24 24" fill="none">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m9 5 7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

  </div>
</template>

<script>
import api from "@/services/api";

const STATUSES = [
  { label: "Approved", color: "#1fa34a" },
  { label: "Unscreened", color: "#f7941d" },
  { label: "Ongoing Screening", color: "#0f2a6b" },
  { label: "Discarded", color: "#dc2626" },
];

const BLOOD_GROUPS = [
  "O positive",
  "O negative",
  "A positive",
  "A negative",
  "B positive",
  "B negative",
  "AB positive",
  "AB negative",
  "Unknown",
];

const AGE_BUCKETS = [
  { label: "18–24", min: 0, max: 24 },
  { label: "25–34", min: 25, max: 34 },
  { label: "35–44", min: 35, max: 44 },
  { label: "45–54", min: 45, max: 54 },
  { label: "55+", min: 55, max: 200 },
];

const PROCESS_GROUPS = [
  { label: "Normal/Regular", color: "#0f2a6b" },
  { label: "Apheresis", color: "#8e98bd" },
  { label: "Patient Directed", color: "#dc2626" },
];

const processGroup = (process) => {
  const value = (process || "").toLowerCase();
  if (value.includes("apheresis")) return "Apheresis";
  if (value.includes("directed")) return "Patient Directed";
  return "Normal/Regular";
};

const pad = (n) => String(n).padStart(2, "0");
const MONTHS_PER_VIEW = 12;

export default {
  name: "BloodDonationsReportTab",
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
    filters: {
      type: Object,
      default: () => ({
        blood_group: "",
        status: "",
        start_date: "",
        end_date: "",
        donation_type: "",
        technology: "",
      }),
    },
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      perPage: 10,
      loading: false,
      donations: [], // rows for the current page (table)
      allDonations: [], // every row matching the filters (cards and charts)
      selected: [],
      error: null,
      user: null,
      facility_logo: "",
      localSearchQuery: this.searchQuery,
      monthsBack: 0, // how far the "Donations over time" window is shifted back
      dateRange: {
        from: this.filters.start_date || "",
        to: this.filters.end_date || "",
      },
    };
  },
  created() {
    this.getBloodUnitDonations();
    this.user = this.$store?.getters?.["auth/user"];
    this.facility_logo =
      this.user?.user_roles?.[0]?.facility_department?.facility?.logo;
  },
  beforeUnmount() {
    clearTimeout(this.fetchTimer);
  },
  computed: {
    /* ----------------------------- headline ----------------------------- */
    totalDonations() {
      return this.allDonations.length;
    },
    statusSummary() {
      return STATUSES.map((s) => ({
        ...s,
        count: this.allDonations.filter((r) => r.status === s.label).length,
      }));
    },
    approvedPct() {
      const approved = this.statusSummary[0].count;
      return this.totalDonations
        ? Math.round((approved / this.totalDonations) * 100)
        : 0;
    },

    /* -------------------------- donations over time ------------------------ */
    // the most recent month that has data (or the current month when empty)
    latestMonthKey() {
      if (!this.allDonations.length) {
        const now = new Date();
        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
      }
      return this.allDonations
        .map((r) => this.monthKey(r.date_added))
        .sort()
        .pop();
    },
    // a fixed calendar window of months, shifted back by monthsBack; months
    // without donations simply render as empty columns
    monthly() {
      const [anchorYear, anchorMonth] = this.latestMonthKey.split("-").map(Number);
      const cursor = new Date(
        anchorYear,
        anchorMonth - 1 - this.monthsBack - (MONTHS_PER_VIEW - 1),
        1
      );

      const months = [];
      for (let i = 0; i < MONTHS_PER_VIEW; i++) {
        months.push({
          key: `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`,
          label: cursor.toLocaleString("en-GB", { month: "short" }),
          year: cursor.getFullYear(),
        });
        cursor.setMonth(cursor.getMonth() + 1);
      }

      return months.map((month, idx) => {
        const rows = this.allDonations.filter(
          (r) => this.monthKey(r.date_added) === month.key
        );
        return {
          ...month,
          yearStart: idx > 0 && month.year !== months[idx - 1].year,
          total: rows.length,
          segments: STATUSES.map((s) => ({
            ...s,
            count: rows.filter((r) => r.status === s.label).length,
          })),
        };
      });
    },
    // one entry per year, sized by how many months it covers
    yearGroups() {
      return this.monthly.reduce((groups, month) => {
        const last = groups[groups.length - 1];
        if (last && last.year === month.year) last.count++;
        else groups.push({ year: month.year, count: 1 });
        return groups;
      }, []);
    },
    rangeLabel() {
      const first = this.monthly[0];
      const last = this.monthly[this.monthly.length - 1];
      return first && last
        ? `${first.label} ${first.year} – ${last.label} ${last.year}`
        : "";
    },
    chartStep() {
      return this.niceStep(Math.max(0, ...this.monthly.map((m) => m.total)));
    },
    chartMax() {
      return this.chartStep * 4;
    },
    yTicks() {
      return [0, 1, 2, 3, 4].map((i) => i * this.chartStep);
    },

    /* ---------------------------- blood groups ----------------------------- */
    bloodGroupRows() {
      return BLOOD_GROUPS.map((label) => ({
        label,
        count: this.allDonations.filter((r) => r.blood_group === label).length,
      }));
    },
    bloodGroupMax() {
      return Math.max(1, ...this.bloodGroupRows.map((r) => r.count));
    },

    /* ------------------- status / gender / process / age ------------------- */
    breakdowns() {
      const total = this.totalDonations;
      const build = (title, defs, countFn) => ({
        title,
        items: defs.map((d) => {
          const count = countFn(d);
          return {
            label: d.label,
            color: d.color,
            count,
            percent: total ? Math.round((count / total) * 100) : 0,
            share: total ? (count / total) * 100 : 0,
          };
        }),
      });

      return [
        build("Status", STATUSES, (d) =>
          this.allDonations.filter((r) => r.status === d.label).length
        ),
        build(
          "Gender",
          [
            { label: "Female", color: "#f7941d" },
            { label: "Male", color: "#0f2a6b" },
          ],
          (d) => this.allDonations.filter((r) => r.gender === d.label).length
        ),
        build("Donation process", PROCESS_GROUPS, (d) =>
          this.allDonations.filter((r) => processGroup(r.process) === d.label).length
        ),
      ];
    },
    ageRows() {
      return AGE_BUCKETS.map((b) => ({
        label: b.label,
        count: this.allDonations.filter((r) => {
          const age = Number(r.age);
          return age >= b.min && age <= b.max;
        }).length,
      }));
    },
    ageMax() {
      return Math.max(1, ...this.ageRows.map((r) => r.count));
    },

    /* ------------------------------- table -------------------------------- */
    allSelected() {
      return (
        this.donations.length > 0 &&
        this.donations.every((d) => this.selected.includes(d.donation_id))
      );
    },
    pageItems() {
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

      let start = Math.max(2, current - 1);
      let end = Math.min(total - 1, current + 1);
      if (current <= 3) {
        start = 2;
        end = 4;
      }
      if (current >= total - 2) {
        start = total - 3;
        end = total - 1;
      }

      const items = [1];
      if (start > 2) items.push("…");
      for (let i = start; i <= end; i++) items.push(i);
      if (end < total - 1) items.push("…");
      items.push(total);
      return items;
    },
  },
   watch: {
     searchQuery(newVal) {
       this.localSearchQuery = newVal;
     },
     localSearchQuery(newVal) {
       this.$emit("update:searchQuery", newVal);
       this.scheduleFetch();
     },
     filters: {
       handler(filters) {
         // Update dateRange to match filters prop, but do not trigger fetch
         this.dateRange = {
           from: filters.start_date || "",
           to: filters.end_date || "",
         };
       },
       deep: true,
     },
   },
  methods: {
    /* ------------------------------ helpers ------------------------------- */
    formatDate(date) {
      if (!date) return "N/A";
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        console.error("Invalid date:", date);
        return "N/A";
      }
      return parsedDate.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    },
    monthKey(date) {
      const d = new Date(date);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    },
    // round max/4 up to a whole-number step so ticks are 0, s, 2s, 3s, 4s
    niceStep(max) {
      if (max <= 0) return 1;
      const rough = max / 4;
      const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
      const normalized = rough / magnitude;
      const nice = [1, 2, 3, 4, 5, 10].find((n) => n >= normalized) || 10;
      return Math.max(1, nice * magnitude);
    },
     statusClass(status) {
       switch (status) {
         case "Approved":
           return "text-green-600 bg-green-100";
         case "Unscreened":
           return "text-orange-600 bg-orange-100";
         case "Ongoing Screening":
           return "text-[#0f2a6b] bg-[#dbe3f7]";
         case "Discarded":
         case "Rejected":
           return "text-red-600 bg-red-100";
         default:
           return "text-slate-600 bg-slate-100";
       }
     },
    toggleAll(event) {
      const ids = this.donations.map((d) => d.donation_id);
      this.selected = event.target.checked
        ? Array.from(new Set([...this.selected, ...ids]))
        : this.selected.filter((id) => !ids.includes(id));
    },
    // one debounced reload for search, date range and filter changes
    scheduleFetch() {
      this.currentPage = 1;
      this.monthsBack = 0;
      clearTimeout(this.fetchTimer);
      this.fetchTimer = setTimeout(() => this.getBloodUnitDonations(), 300);
    },
    earlierMonths() {
      this.monthsBack += MONTHS_PER_VIEW;
    },
    laterMonths() {
      this.monthsBack = Math.max(0, this.monthsBack - MONTHS_PER_VIEW);
     },
applyFilters(filters, searchQuery) {
        this.$emit("update:filters", { ...this.filters, ...filters });
        // Sync dateRange immediately so the fetch below uses the new dates
        // (the filters watcher only runs on the next tick, after this call).
        this.dateRange = {
          from: filters.start_date || "",
          to: filters.end_date || "",
        };
        // Set the search term synchronously too — the localSearchQuery watcher
        // fires on the next tick and would otherwise queue a second, stale fetch.
        if (searchQuery !== undefined) {
          clearTimeout(this.fetchTimer);
          this.localSearchQuery = searchQuery;
        }
        this.currentPage = 1;
        // Pass the resolved filters directly: the filters prop is still the
        // old values until Vue flushes the update:filters emit.
        this.getBloodUnitDonations({ filters, searchQuery: this.localSearchQuery });
      },

    /* ------------------------------- data --------------------------------- */
     // Maps one API record to the flat row shape the template uses.
     // Adjust the age / gender fields to match your backend.
     normalizeDonation(d) {
       const user =
         d.donor?.user_donor_questionnaire?.user || d.donor?.user || {};
       const dob = user.dob;
       const age =
         dob ? Math.floor((Date.now() - new Date(dob)) / 31557600000) : "";
       // Map backend status to frontend labels
       const statusMap = {
         Disposed: 'Discarded',
         Deferred: 'Ongoing Screening',
       };
       const status = statusMap[d.status] || d.status || 'N/A';
       return {
         donation_id: d.donation_id,
         serial: d.pint_serial || "N/A",
         donor_name:
           `${user.first_name || ""} ${user.last_name || ""}`.trim() || "N/A",
         age,
         gender: user.gender || "",
         blood_group: d.blood_group?.description || "Unknown",
         process: d.technology || "N/A",
         date_added: d.date_added,
         status,
       };
     },
    // Table: always real, paginated data from the API
    async getBloodUnitDonations({ refreshAnalytics = true, filters = null, searchQuery = null } = {}) {
      this.loading = true;
      this.error = null;
      try {
        const f = filters || this.filters;
        const q = searchQuery !== null ? searchQuery : this.localSearchQuery;
        const queryParams = {
          blood_group: f.blood_group,
          status: f.status,
          start_date: this.dateRange.from,
          end_date: this.dateRange.to,
          donation_type: f.donation_type,
          technology: f.technology,
          search_query: q,
          page: this.currentPage,
          per_page: this.perPage,
        };

        const [response] = await Promise.all([
          api.getBloodDonationsReport(queryParams),
          refreshAnalytics ? this.loadAnalytics({ filters: f, searchQuery: q }) : null,
        ]);

        this.donations = (response.data || []).map(this.normalizeDonation);
        this.currentPage = response.current_page || 1;
        this.totalPages = response.total_pages || 1;
        // use whichever total your API returns; falls back to the page size
        this.totalRecords =
          response.total ??
          response.total_records ??
          response.meta?.total ??
          this.donations.length;
      } catch (error) {
        this.error = error.message || "Failed to fetch donations";
        console.error("Error fetching donations:", error);
      } finally {
        this.loading = false;
      }
    },
    async loadAnalytics({ filters = null, searchQuery = null } = {}) {
      const f = filters || this.filters;
      const q = searchQuery !== null ? searchQuery : this.localSearchQuery;
      const response = await api.getBloodDonationsReport({
        blood_group: f.blood_group,
        status: f.status,
        start_date: this.dateRange.from,
        end_date: this.dateRange.to,
        donation_type: f.donation_type,
        technology: f.technology,
        search_query: q,
        all: true,
      });
      this.allDonations = (response.data || []).map(this.normalizeDonation);
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.getBloodUnitDonations({ refreshAnalytics: false });
      }
    },
    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.getBloodUnitDonations({ refreshAnalytics: false });
      }
    },
    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        await this.getBloodUnitDonations({ refreshAnalytics: false });
      }
    },

    /* ------------------------------ print report --------------------------- */
    async handleDownloadDonationReport() {
      const printWindow = document.createElement("iframe");
      printWindow.style.position = "absolute";
      printWindow.style.width = "0";
      printWindow.style.height = "0";
      document.body.appendChild(printWindow);

      try {
        const response = await api.getBloodDonationsReport({
          blood_group: this.filters.blood_group,
          status: this.filters.status,
          start_date: this.dateRange.from,
          end_date: this.dateRange.to,
          search_query: this.localSearchQuery,
          all: true,
        });
        const donations = (response.data || []).map(this.normalizeDonation);

        const tableRows = donations
          .map(
            (data, index) => `
          <tr class="text-sm border-b">
            <td class="px-3 py-1">${index + 1}</td>
            <td class="px-3 py-1">${data.serial || "N/A"}</td>
            <td class="px-3 py-1">${data.donor_name || "N/A"}</td>
            <td class="px-3 py-1">${data.age || "N/A"}</td>
            <td class="px-3 py-1">${data.gender || "N/A"}</td>
            <td class="px-3 py-1">${data.blood_group || "Unknown"}</td>
            <td class="px-3 py-1">${data.process || "N/A"}</td>
            <td class="px-3 py-1">${this.formatDate(data.date_added)}</td>
            <td class="px-3 py-1">${data.status || "N/A"}</td>
          </tr>
        `
          )
          .join("");

        const printContent = `
          <div class="page-container">
            <div class="report-container">
              <div class="logo-container">
                <img src="${this.facility_logo}" alt="Hospital Logo" class="logo">
              </div>
              <br><br>
              <h1 class="mb-4 text-lg font-bold">Donations Report</h1>
              <p class="mb-4">Generated on: ${new Date().toLocaleString()}</p>
              <div class="table-container">
                <table class="w-full text-left">
                  <thead>
                    <tr class="bg-gray-100">
                      <th class="px-3 py-2">#</th>
                      <th class="px-3 py-2">Bag Number</th>
                      <th class="px-3 py-2">Donor Name</th>
                      <th class="px-3 py-2">Age</th>
                      <th class="px-3 py-2">Gender</th>
                      <th class="px-3 py-2">Blood Group</th>
                      <th class="px-3 py-2">Donation Process</th>
                      <th class="px-3 py-2">Donation Date</th>
                      <th class="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${tableRows}
                  </tbody>
                </table>
              </div>
              <div class="mt-8 signatures">
                <div class="signature-line">
                  <p>Prepared by: _________________</p>
                  <p>Date: _________________</p>
                </div>
                <div class="mt-4 signature-line">
                  <p>Verified by: _________________</p>
                  <p>Date: _________________</p>
                </div>
              </div>
            </div>
          </div>
        `;

        const printDocument = `
          <html>
          <head>
            <title>Donations Report</title>
            <style>
              @media print {
                @page {
                  size: A4;
                  margin: 2cm;
                }
              }
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                color: #333;
                margin: 0;
                padding: 0;
              }
              .logo {
                width: 120px;
                float: right;
              }
              .page-container {
                padding: 20px;
              }
              .report-container {
                max-width: 100%;
                margin: 0 auto;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
                font-size: 10.5px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #F3F4F6;
                font-weight: bold;
              }
              .signatures {
                margin-top: 40px;
                page-break-inside: avoid;
                font-size: 10.5px;
              }
              .signature-line {
                margin-bottom: 20px;
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
          </html>
        `;

        printWindow.contentDocument.open();
        printWindow.contentDocument.write(printDocument);
        printWindow.contentDocument.close();

        printWindow.contentWindow.onload = function () {
          printWindow.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(printWindow);
          }, 1000);
        };
      } catch (error) {
        console.error("Error generating report:", error);
      }
    },
  },
};
</script>

<style lang="css" scoped>
/* keep the table header visible while the table body scrolls */
thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f8fafc;
}
</style>